/**
 * salvia-860 (fatia 1 da salvia-840): reconhecer o gatilho de voz "Salvia"
 * num trecho transcrito e separar o comando que vem depois.
 *
 * Função PURA de propósito: o reconhecimento de voz já roda no
 * AppointmentAudioRecorder, e a fatia 2 (salvia-861) vai precisar da mesma regra
 * do lado de lá. Deixar a decisão aqui evita duas cópias divergindo — que é a
 * classe de problema das salvia-823/826.
 *
 * ── Por que o casamento é tolerante ──────────────────────────────────────────
 * O reconhecedor do navegador não escreve "Salvia" de um jeito só. Em pt-BR ele
 * transcreve o que ouve, e "salvia" sai como sálvia (a planta, que ele conhece),
 * salva, sávia, ou até "sal via" em dois pedaços. Casamento exato falharia na
 * primeira consulta real.
 *
 * ── Por que só no INÍCIO do trecho ───────────────────────────────────────────
 * "Salvia" é o nome do produto: o profissional vai dizer isso naturalmente no
 * meio de uma frase ("o Salvia não está abrindo hoje"). Exigir o gatilho no
 * começo de um trecho final reduz muito o falso positivo, porque comandos são
 * ditos como comandos — com pausa antes.
 *
 * Não elimina, e não precisa eliminar: pela decisão de 14/09, cada comando vira
 * uma PROPOSTA que o médico aceita ou recusa (salvia-861). O custo de um falso
 * positivo é uma linha recusada na revisão; o custo de um falso NEGATIVO é o
 * profissional falar achando que registrou. Por isso, na dúvida, capturamos.
 *
 * ── Casos cobertos ──────────────────────────────────────────────────────────
 * A bancada e `tests/test_comandos_de_voz.mjs` (`node tests/test_comandos_de_voz.mjs`).
 * Esta tabela ficou DESATUALIZADA uma vez — descrevia o comportamento ancorado
 * em `^` depois de ele ter sido removido, dizendo `null` para casos que o codigo
 * ja casava. Quem mudar a regra muda a bancada; esta lista e resumo, nao fonte.
 *
 *   "Salvia, anote dor lombar"          -> "anote dor lombar"       · registro
 *   "Sálvia anote dor lombar"           -> "anote dor lombar"       · registro
 *   "salva, registrar peso 82 quilos"   -> "registrar peso 82..."   · registro
 *   "Sal via anotar meta de caminhada"  -> "anotar meta de..."      · registro
 *   "o Salvia não está abrindo"         -> "não está abrindo"       · NAO e registro
 *   "o Salvia está lento hoje"          -> "está lento hoje"        · NAO e registro
 *   "manda pelo Salvia"                 -> null  (nada apos o gatilho)
 *   "Salvia"                            -> null  (sem comando)
 *   "Salvia."                           -> null  (sem comando)
 */

/**
 * As formas que o reconhecedor produz para "Salvia", como UMA regex sobre o texto
 * ORIGINAL (não sobre uma versão normalizada).
 *
 * Por que assim: a primeira versão desta função normalizava o texto, casava a
 * variante e recortava o comando pelo TAMANHO da variante normalizada. Isso
 * quebra quando o original tem espaço a mais ("Sal  via"), porque o normalizado
 * colapsa espaços e os índices deixam de corresponder. Casando direto no
 * original, o recorte usa o tamanho do que foi REALMENTE casado.
 *
 *   s[aá]lvia    -> salvia, sálvia
 *   s[aá]l\s+via -> "sal via" (o reconhecedor às vezes separa)
 *   s[aá]via     -> sávia, savia
 *   salva|salve  -> o que o reconhecedor de fato escreveu em stage (15/09)
 *
 * ⚠️ NÃO exige mais o gatilho no INÍCIO do trecho — e essa mudança veio de uso
 * real. A primeira versão ancorava em `^` para evitar o falso positivo de quem
 * diz o nome do produto no meio de uma frase. Mas a suposição por trás disso — de
 * que cada trecho final do reconhecedor é uma fala curta, e que o comando viria
 * como fala própria — é falsa: em stage, uma consulta inteira veio praticamente
 * num trecho só, sem pontuação, com o gatilho na posição 422 de 565 caracteres.
 * Ancorado no início, o detector NUNCA disparava.
 *
 * A troca é deliberada e depende do desenho decidido em 15/09: o chip é apenas
 * RETORNO VISUAL, e quem gera proposta é o agente do fim da consulta. Com isso,
 * PERDER um comando é pior que mostrar um a mais — o extra custa um clique para
 * remover; o perdido o profissional nem sabe que existiu.
 */
const GATILHO = /(s[aá]lvia|s[aá]l\s+via|s[aá]via|salv[ae])\b[\s,.:;!?\u2010-\u2015-]*/i;

/** Comando mais curto que aceitamos, em caracteres. Evita ruído solto. */
const MINIMO_DO_COMANDO = 3;

/**
 * Verbos que marcam um PEDIDO DE REGISTRO — a camada 1 do desenho de 22/09.
 *
 * ── Por que isto existe ──────────────────────────────────────────────────────
 * O gatilho sozinho não separa comando de comentário: "Salvia" é o nome do
 * produto, e o detector (que não exige mais o gatilho no início) casa igual em
 * "o Salvia está lento hoje". Enquanto o comando só virava um chip descartável,
 * isso custava um clique. Com a PROPOSTA IMEDIATA passa a custar uma chamada ao
 * modelo e um card espúrio nas Notas Salvas, a cada vez que alguém disser o nome
 * do sistema em voz alta.
 *
 * O discriminador não é a POSIÇÃO do gatilho — é o que vem DEPOIS dele. O
 * próprio agente já enuncia a regra ("Comando é pedido de registro"); ela só
 * nunca tinha sido implementada aqui.
 *
 * ── Por que uma lista curta e explícita ──────────────────────────────────────
 * Decisão do Ariel em 22/09. A alternativa (qualquer verbo no imperativo) pega
 * mais casos e é muito mais difícil de justificar quando erra. Esta lista é
 * auditável: dá para ler e dizer por que um comando entrou ou não.
 *
 * ── Por que errar para menos aqui é barato ───────────────────────────────────
 * Isto inverte o trade-off de 15/09, e a inversão é deliberada. O comentário
 * acima diz "na dúvida, capturamos", e continua valendo para o CHIP. Para a
 * PROPOSTA, não: o agente do fim da consulta varre a transcrição inteira e pega
 * o que aqui não passou. Um comando que não vira proposta imediata não se perde
 * — só chega mais tarde. Um card espúrio, esse, já nasceu no prontuário.
 *
 * O segundo sinal — medida ditada sem verbo — está logo abaixo.
 */
const VERBOS_DE_REGISTRO = [
  "anota", "anote", "anotar",
  "registra", "registre", "registrar",
  "salva", "salve", "salvar",
  "marca", "marque", "marcar",
];

/**
 * SEGUNDO SINAL: número + palavra de um assunto clínico (decisão do Ariel, 22/09).
 *
 * O verbo sozinho deixava de fora o caso que motivou a funcionalidade: o
 * profissional mede a paciente longe do computador e dita o valor, sem pedir
 * licença — "Salvia, peso 82 e meio, altura 1 e 68". Não há verbo ali, e é
 * inequivocamente um pedido de registro.
 *
 * ── Por que EXIGIR o número ──────────────────────────────────────────────────
 * É ele que segura a precisão. Sem o número, "peso" bastaria e "Salvia, o peso
 * do sistema está grande" viraria proposta. Os dois falsos positivos reais que
 * motivaram a camada 1 ("o Salvia está lento hoje", "o Salvia não está abrindo")
 * não têm número nenhum, e continuam de fora.
 *
 * ── Esta lista NÃO escolhe o field_key ───────────────────────────────────────
 * Quem escolhe é o agente, que lê a conversa. Aqui a pergunta é só "isto é
 * pedido de registro?". Por isso as palavras podem ser grossas: basta que
 * indiquem que se está falando de um assunto clínico, não de software.
 *
 * ── Errar aqui é aceitável, e por quê ────────────────────────────────────────
 * Decisão do Ariel: se a leitura estiver errada, a proposta nasce e o médico
 * NEGA. Nada é gravado sem aprovação — o card existe exatamente para isso. O
 * custo de um extra é um clique; o de um perdido é o profissional achar que
 * registrou.
 *
 * ⚠️ Só pega número em DÍGITO. "oitenta e dois quilos" por extenso não dispara e
 * cai na rede do fim da consulta. O reconhecedor escreve medida em dígito na
 * esmagadora maioria dos casos, e aceitar numeral por extenso exigiria um
 * parser de números — caro para o ganho.
 */
const PALAVRAS_DE_ASSUNTO_CLINICO = [
  // medidas do corpo
  "peso", "pesa", "pesou", "quilo", "quilos", "kg",
  "altura", "metro", "metros", "cm", "centimetro", "centimetros", "imc",
  // hidratação
  "agua", "litro", "litros",
  // sono
  "sono", "dorme", "dormiu", "dormindo",
  // exercício
  "exercicio", "exercicios", "caminhada", "treino", "academia", "corrida",
  "minuto", "minutos", "passo", "passos",
  // álcool
  "alcool", "bebida", "bebidas", "dose", "doses",
  // medicação e suplemento
  "remedio", "remedios", "medicamento", "medicamentos", "comprimido",
  "comprimidos", "capsula", "capsulas", "suplemento", "suplementos",
  "mg", "ml",
  // queixas e exames com valor
  "dor", "dores", "febre", "pressao", "glicemia", "colesterol",
];

/** Tira acento para a comparação, sem tocar no texto que será enviado. */
function semAcento(texto) {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * O comando é um pedido de registro?
 *
 * Olha a PRIMEIRA palavra, não o texto todo: comando ditado começa pelo verbo
 * ("anota o que ela falou"), e procurar o verbo em qualquer posição traria de
 * volta o falso positivo por outra porta — "o Salvia não salva nada hoje" tem
 * "salva" no meio e não é pedido de registro.
 *
 * @param {string} comando O texto DEPOIS do gatilho.
 * @returns {boolean}
 */
export function ehPedidoDeRegistro(comando) {
  const texto = semAcento(String(comando || "").trim().toLowerCase());
  if (!texto) return false;

  // ── Sinal 1: verbo de registro ──
  // "por favor anota ..." — cortesia antes do verbo é comum na fala.
  const semCortesia = texto.replace(/^(por\s+favor|pf)[\s,]+/, "");
  const palavras = semCortesia.split(/[\s,.:;!?]+/).filter(Boolean);

  if (VERBOS_DE_REGISTRO.includes(palavras[0] || "")) return true;

  // ── Sinal 2: medida ditada (número + assunto clínico) ──
  // A palavra é procurada em QUALQUER posição, ao contrário do verbo: "peso 82"
  // e "82 quilos" são as duas formas naturais de ditar, e nenhuma põe a palavra
  // num lugar fixo. O número é o que segura a precisão aqui.
  if (!/\d/.test(texto)) return false;

  return palavras.some((p) => PALAVRAS_DE_ASSUNTO_CLINICO.includes(p));
}

/**
 * Extrai o comando de voz de um trecho da transcrição.
 *
 * @param {string} trecho Texto reconhecido.
 * @returns {{comando: string, gatilho: string, ehRegistro: boolean} | null} null
 *          quando não há gatilho, ou quando não sobra comando depois dele.
 *
 * `ehRegistro` separa as duas camadas e NÃO filtra nada aqui de propósito: quem
 * mostra o chip continua querendo tudo (é retorno visual — "o sistema te ouviu"),
 * e só quem cria a proposta imediata olha a flag. Filtrar aqui apagaria o chip
 * de um comando legítimo que a lista de verbos não cobre, e o profissional
 * ficaria sem saber se foi ouvido.
 */
export function extrairComando(trecho) {
  const bruto = String(trecho || "");
  const casou = bruto.match(GATILHO);
  if (!casou) return null;

  // A partir do FIM do que casou — não do início do trecho.
  const comando = bruto.slice(casou.index + casou[0].length).trim();
  if (comando.length < MINIMO_DO_COMANDO) return null;

  return {
    comando,
    gatilho: casou[1].toLowerCase(),
    ehRegistro: ehPedidoDeRegistro(comando),
  };
}

export const _internos = {
  GATILHO,
  MINIMO_DO_COMANDO,
  VERBOS_DE_REGISTRO,
  PALAVRAS_DE_ASSUNTO_CLINICO,
};
